import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FiSave, FiPlus, FiTrash2, FiLink, FiImage } from "react-icons/fi";
import { apiRequest, apiFormRequest, API_BASE_URL } from "../../services/api";

const fetchFooterSettings = async () => {
    const { data } = await apiRequest("/settings/footer_config");
    // Ensure we return the 'value' part of the settings object
    return data && data.value ? (typeof data.value === 'string' ? JSON.parse(data.value) : data.value) : {};
};

const updateFooterSettings = async (formData) => {
    const { data } = await apiFormRequest("/settings/footer_config", formData, {
        method: "PUT"
    });
    return data;
};

// Helper: Get Image URL
const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_BASE_URL.replace('/api', '')}/${path.replace(/\\/g, '/')}`;
};

const FooterManager = () => {
    const queryClient = useQueryClient();

    const { register, control, handleSubmit, reset, watch } = useForm({
        defaultValues: {
            copyright_text: "© 2024 Osama Ali. All rights reserved.",
            social_links: [],
            menu_columns: []
        }
    });

    // Dynamic Fields for Social Links
    const { fields: socialFields, append: appendSocial, remove: removeSocial } = useFieldArray({
        control,
        name: "social_links"
    });

    // Dynamic Fields for Menu Columns
    const { fields: columnFields, append: appendColumn, remove: removeColumn } = useFieldArray({
        control,
        name: "menu_columns"
    });

    const { isLoading } = useQuery({
        queryKey: ["footer_settings"],
        queryFn: fetchFooterSettings,
        onSuccess: (data) => {
            reset(data); // Populate form
        }
    });

    const mutation = useMutation({
        mutationFn: updateFooterSettings,
        onSuccess: () => {
            queryClient.invalidateQueries(["footer_settings"]);
            toast.success("Footer settings saved successfully!");
        },
        onError: (err) => {
            toast.error(err.response?.data?.message || "Failed to save settings");
        }
    });

    const onSubmit = (data) => {
        const formData = new FormData();

        // Append Logo
        if (data.logo_file && data.logo_file[0]) {
            formData.append("logo", data.logo_file[0]);
        }

        // Clean data before sending (remove file object from main data)
        const { logo_file, ...rest } = data;

        // Convert the rest of the data to JSON string or send as keys
        // Since we have nested arrays, JSON string for 'data' key is safer/simpler for our backend handler
        formData.append("data", JSON.stringify(rest));

        mutation.mutate(formData);
    };

    const logoPreview = watch("logo");

    if (isLoading) return <div className="p-4">Loading footer settings...</div>;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 p-4 bg-white dark:bg-boxdark rounded-sm border border-stroke dark:border-strokedark shadow-default">

            {/* 1. Logo Section */}
            <div className="border-b border-stroke dark:border-strokedark pb-6">
                <h3 className="text-lg font-semibold mb-4 text-black dark:text-white flex items-center gap-2">
                    <FiImage /> Logo Settings
                </h3>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="w-full md:w-1/2">
                        <label className="mb-2.5 block font-medium text-black dark:text-white">Upload Logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            {...register("logo_file")}
                            className="w-full cursor-pointer rounded-lg border-[1.5px] border-stroke bg-transparent font-medium outline-none transition file:mr-5 file:border-collapse file:cursor-pointer file:border-0 file:border-r file:border-solid file:border-stroke file:bg-whiter file:py-3 file:px-5 file:hover:bg-primary file:hover:bg-opacity-10 focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:file:border-form-strokedark dark:file:bg-white/30 dark:file:text-white dark:focus:border-primary"
                        />
                        <p className="text-sm text-gray-500 mt-2">Recommended size: 200x50px (SVG or PNG)</p>
                    </div>
                    {logoPreview && (
                        <div className="w-full md:w-1/2 flex flex-col items-center p-4 bg-gray-50 dark:bg-meta-4 rounded">
                            <span className="text-sm mb-2 text-gray-500">Current Logo:</span>
                            <img
                                src={getImageUrl(logoPreview)}
                                alt="Footer Logo"
                                className="max-h-20 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 2. Copyright Section */}
            <div className="border-b border-stroke dark:border-strokedark pb-6">
                <h3 className="text-lg font-semibold mb-4 text-black dark:text-white">Copyright Text</h3>
                <textarea
                    rows={2}
                    {...register("copyright_text")}
                    className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                    placeholder="© 2024 Your Name. All rights reserved."
                ></textarea>
            </div>

            {/* 3. Social Links Section */}
            <div className="border-b border-stroke dark:border-strokedark pb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-black dark:text-white">Social Media Links</h3>
                    <button
                        type="button"
                        onClick={() => appendSocial({ platform: "", icon_class: "", url: "" })}
                        className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            <FiPlus className="transition-transform duration-300 group-hover:rotate-90" />
                            Add Link
                        </span>
                        <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </button>
                </div>

                {socialFields.length === 0 && <p className="text-gray-500 italic">No social links added.</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {socialFields.map((field, index) => (
                        <div key={field.id} className="flex gap-2 items-start bg-gray-50 dark:bg-meta-4/30 p-3 rounded border border-stroke dark:border-strokedark">
                            <div className="flex-1 space-y-2">
                                <input
                                    {...register(`social_links.${index}.platform`)}
                                    placeholder="Platform Name (e.g. LinkedIn)"
                                    className="w-full rounded border border-stroke bg-white dark:bg-boxdark py-1 px-2 text-sm outline-none focus:border-primary dark:border-strokedark"
                                />
                                <input
                                    {...register(`social_links.${index}.icon_class`)}
                                    placeholder="Icon Class (e.g. fa-brands fa-linkedin)"
                                    className="w-full rounded border border-stroke bg-white dark:bg-boxdark py-1 px-2 text-sm outline-none focus:border-primary dark:border-strokedark"
                                />
                                <input
                                    {...register(`social_links.${index}.url`)}
                                    placeholder="URL (https://...)"
                                    className="w-full rounded border border-stroke bg-white dark:bg-boxdark py-1 px-2 text-sm outline-none focus:border-primary dark:border-strokedark"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => removeSocial(index)}
                                className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                                title="Remove Link"
                            >
                                <FiTrash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* 4. Menu Columns */}
            <div className="pb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-black dark:text-white">Footer Menu Columns</h3>
                    <button
                        type="button"
                        onClick={() => appendColumn({ title: "New Column", links: [] })}
                        className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            <FiPlus className="transition-transform duration-300 group-hover:rotate-90" />
                            Add Column
                        </span>
                        <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </button>
                </div>

                {columnFields.length === 0 && <p className="text-gray-500 italic">No menu columns added.</p>}

                <div className="space-y-6">
                    {columnFields.map((field, columnIndex) => (
                        <MenuColumn
                            key={field.id}
                            control={control}
                            register={register}
                            columnIndex={columnIndex}
                            removeColumn={removeColumn}
                        />
                    ))}
                </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-boxdark bg-opacity-90 backdrop-blur pt-4 border-t border-stroke dark:border-strokedark">
                <button
                    type="submit"
                    disabled={mutation.isLoading}
                    className="group relative flex w-full justify-center overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary p-3 font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95 disabled:opacity-50"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        {mutation.isLoading ? "Saving..." : "Save Footer Settings"}
                        {!mutation.isLoading && <FiSave className="ml-2 mt-1" />}
                    </span>
                    <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </button>
            </div>
        </form>
    );
};

// Sub-component for Menu Column to manage its own nested array of links cleaner
const MenuColumn = ({ control, register, columnIndex, removeColumn }) => {
    const { fields: linkFields, append: appendLink, remove: removeLink } = useFieldArray({
        control,
        name: `menu_columns.${columnIndex}.links`
    });

    return (
        <div className="bg-white dark:bg-boxdark border border-stroke dark:border-strokedark rounded p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4 border-b border-stroke dark:border-strokedark pb-2">
                <div className="flex-1 mr-4">
                    <input
                        {...register(`menu_columns.${columnIndex}.title`)}
                        placeholder="Column Title (e.g. Resources)"
                        className="font-semibold text-black dark:text-white bg-transparent border-none outline-none focus:ring-0 placeholder-gray-400 w-full"
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => appendLink({ label: "", url: "" })}
                        className="group relative flex items-center gap-1 overflow-hidden rounded-md bg-gradient-to-r from-green-500 to-green-600 px-3 py-1 text-xs font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg active:scale-95"
                    >
                        <span className="relative z-10 flex items-center gap-1">
                            <FiPlus /> Add Link
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => removeColumn(columnIndex)}
                        className="group relative flex items-center gap-1 overflow-hidden rounded-md bg-gradient-to-r from-red-500 to-red-600 px-3 py-1 text-xs font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg active:scale-95"
                    >
                        <span className="relative z-10 flex items-center gap-1">
                            <FiTrash2 /> Column
                        </span>
                    </button>
                </div>
            </div>

            <div className="space-y-2 pl-4 border-l-2 border-gray-100 dark:border-strokedark">
                {linkFields.map((link, linkIndex) => (
                    <div key={link.id} className="flex gap-2 items-center">
                        <FiLink className="text-gray-400" size={14} />
                        <input
                            {...register(`menu_columns.${columnIndex}.links.${linkIndex}.label`)}
                            placeholder="Link Label"
                            className="flex-1 rounded border border-stroke bg-transparent py-1 px-2 text-sm outline-none focus:border-primary dark:border-strokedark"
                        />
                        <input
                            {...register(`menu_columns.${columnIndex}.links.${linkIndex}.url`)}
                            placeholder="URL / Route"
                            className="flex-1 rounded border border-stroke bg-transparent py-1 px-2 text-sm outline-none focus:border-primary dark:border-strokedark"
                        />
                        <button
                            type="button"
                            onClick={() => removeLink(linkIndex)}
                            className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                            title="Remove Link"
                        >
                            <FiTrash2 size={14} />
                        </button>
                    </div>
                ))}
                {linkFields.length === 0 && <p className="text-xs text-gray-400">No links in this column.</p>}
            </div>
        </div>
    );
};

export default FooterManager;
